import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

// ==========================================
// COMPONENTE: PIZARRA DE FIRMA (CANVAS)
// ==========================================
function FirmaPad({ onSave, onClear }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#1e293b";
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onClear();
  };

  return (
    <div className="flex flex-col items-center gap-3 w-full my-4">
      <div className="bg-white/60 backdrop-blur-md border border-slate-300 rounded-2xl shadow-inner overflow-hidden w-full max-w-[400px] h-[180px] relative touch-none">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="absolute top-3 left-4 text-[10px] font-black text-slate-400 uppercase tracking-widest pointer-events-none select-none">
          Firma de quien diligenció
        </div>
      </div>
      <button 
        onClick={handleClear}
        type="button"
        className="text-[10px] uppercase tracking-wider font-black text-slate-500 hover:text-red-500 transition-colors cursor-pointer bg-white/50 px-4 py-1.5 rounded-full border border-slate-200 shadow-sm"
      >
        Limpiar Firma
      </button>
    </div>
  );
}

// ==========================================
// COMPONENTE: PORTAL PARA MODALES
// ==========================================
function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    div.setAttribute("data-portal-modal", "true");
    div.setAttribute("translate", "no");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

// ==========================================
// CONSTANTES
// ==========================================
const LISTA_PELIGROS = ["FISICOS", "BIOLOGICOS", "CONDICIONES DE SEGURIDAD", "BIOMECANICOS", "NATURALES", "QUIMICOS", "PSICOSOCIALES"];

const LISTA_ACTOS = [
  "Manipuacion incorrecta de mercancia", "No seguir procedimiento de seguridad",
  "Falta de atención", "No realizar pausas activas", "Omitir el uso de EPP",
  "Uso de dispositivos moviles en la conduccion", "Correr en el lugar de trabajo",
  "Conducir a gran velocidad", "Bromas o juegos pesados", "Exceder los limites de velocidad",
  "Mal uso de herramientas de trabajo", "Adoptar posiciones inseguras"
];

const LISTA_CONDICIONES = [
  "Infraestructura", "Epps inadecuados", "sistemas electricos", "Iluminacion deficiente",
  "Condiciones de la via", "Falta de orden y aseo en el área", "Pisos mojados", 
  "Condiciones climatologicas", "Clasificación inadecuada de residuos", 
  "Deficiencia o ausencia de señalización.", "Condiciones del vehiculo"
];

// ==========================================
// FORMULARIO PRINCIPAL
// ==========================================
export default function ReporteIncidentesSST({ usuario, onBack }) {
  const nombreUsuario = usuario?.nombres || usuario?.nombre || '';

  const [datos, setDatos] = useState({
    pdv: "",
    fecha: new Date().toISOString().split('T')[0],
    nombre_reporta: nombreUsuario,
    cargo_reporta: "Cargando...",
    tipo_evento: "", 
    descripcion: "",
    peligros: [],
    peligro_otro: "",
    actos: [],
    acto_otro: "",
    condiciones: [],
    condicion_otra: "",
    accion_control: "", 
    plan_accion: "",
    responsable_ejecucion: "",
    fecha_ejecucion: "",
    seguimiento: "",
    estado_seguimiento: "Abierta" 
  });

  const [listaSedes, setListaSedes] = useState([]);
  const [firmaBase64, setFirmaBase64] = useState(null);
  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });

  // Cargar sedes dinámicas desde la base de datos y el cargo del empleado al cargar el componente
  useEffect(() => {
    const cargarDatosMaestros = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL;
        
        // Cargar sedes desde la base de datos maestra
        const resSedes = await fetch(`${API_URL}/api/sedes`);
        const dataSedes = await resSedes.json();
        if (dataSedes.success && Array.isArray(dataSedes.sedes)) {
          setListaSedes(dataSedes.sedes);
        }
      } catch (error) {
        console.error("Error al cargar sedes maestras:", error);
      }
    };
    cargarDatosMaestros();

    if (usuario && usuario.documento) {
      const fetchCargoOficial = async () => {
        try {
          const API_URL = process.env.REACT_APP_API_URL;
          const res = await fetch(`${API_URL}/api/empleados/${usuario.documento}`);
          const data = await res.json();
          
          if (data.success && data.empleado && data.empleado.cargo) {
            setDatos(prev => ({ ...prev, cargo_reporta: data.empleado.cargo }));
          } else {
            setDatos(prev => ({ ...prev, cargo_reporta: usuario.cargo || "NO REGISTRADO" }));
          }
        } catch (error) {
          console.error("Error buscando cargo oficial:", error);
          setDatos(prev => ({ ...prev, cargo_reporta: usuario.cargo || "ERROR DE CONEXIÓN" }));
        }
      };
      fetchCargoOficial();
    }
  }, [usuario]);

  const handleChange = (e) => {
    setDatos({ ...datos, [e.target.name]: e.target.value });
  };

  const handleCheckbox = (listaProp, valor) => {
    setDatos(prev => {
      const listaActual = prev[listaProp];
      if (listaActual.includes(valor)) {
        return { ...prev, [listaProp]: listaActual.filter(item => item !== valor) };
      } else {
        return { ...prev, [listaProp]: [...listaActual, valor] };
      }
    });
  };

  const handleSubmit = async () => {
    if (!datos.pdv.trim()) {
      setCustomAlert({ show: true, message: "Por favor selecciona el Punto de Venta o Sede.", type: "warning" });
      return;
    }
    if (!datos.tipo_evento) {
      setCustomAlert({ show: true, message: "Selecciona si es un Incidente, Acto o Condición.", type: "warning" });
      return;
    }
    if (!datos.descripcion.trim()) {
      setCustomAlert({ show: true, message: "La descripción del evento es obligatoria.", type: "warning" });
      return;
    }
    if (!firmaBase64) {
      setCustomAlert({ show: true, message: "La firma de quien diligenció es obligatoria.", type: "warning" });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_API_URL;

      const payload = {
        tipo_formulario: "REPORTE_INCIDENTES_SST",
        datosGenerales: {
          pdv: datos.pdv.trim(),
          fecha: datos.fecha,
          inspector: datos.nombre_reporta 
        },
        respuestas: {
          cargo_reporta: datos.cargo_reporta,
          tipo_evento: datos.tipo_evento,
          descripcion: datos.descripcion,
          peligros: datos.peligros.join(' / '),
          peligro_otro: datos.peligro_otro,
          actos: datos.actos.join(' / '),
          acto_otro: datos.acto_otro,
          condiciones: datos.condiciones.join(' / '),
          condicion_otra: datos.condicion_otra,
          accion_control: datos.accion_control,
          plan_accion: datos.plan_accion,
          responsable_ejecucion: datos.responsable_ejecucion,
          fecha_ejecucion: datos.fecha_ejecucion,
          seguimiento: datos.seguimiento,
          estado_seguimiento: datos.estado_seguimiento
        },
        observaciones: {},
        puntaje_global: 0,
        evidencias: {
          firma_reportante: firmaBase64
        }
      };

      const res = await fetch(`${API_URL}/api/formularios/guardar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });

      const dataRes = await res.json();
      if (dataRes.success) {
        setCustomAlert({ show: true, message: "¡Reporte FT-SST-11 guardado exitosamente!", type: "success" });
        setTimeout(() => { onBack(); }, 1500);
      } else {
        setCustomAlert({ show: true, message: dataRes.message || "Error al guardar el reporte.", type: "error" });
      }
    } catch (error) {
      console.error("Error de conexión:", error);
      setCustomAlert({ show: true, message: "Error de conexión con el servidor.", type: "error" });
    }
  };

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-5xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      
      {/* CABECERA */}
      <div className="bg-transparent border-b border-white/20 px-4 py-3 sm:px-8 sm:py-5 z-40 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack} 
            className="w-10 h-10 flex-none bg-white/60 hover:bg-white/80 text-slate-700 hover:text-red-500 flex items-center justify-center rounded-full transition-all shadow-sm border border-white/40 cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-slate-800 leading-tight">Reporte Incidentes, Actos y Condiciones Inseguras</h1>
            <span className="bg-amber-500/20 text-amber-700 border border-amber-500/30 text-[10px] font-bold px-2 py-0.5 rounded-md backdrop-blur-sm">FT-SST-11 | V-3</span>
          </div>
        </div>
      </div>

      {/* ÁREA SCROLLABLE DEL FORMULARIO */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-8 space-y-5 relative">
        
        {/* SECCIÓN 1: DATOS GENERALES */}
        <div className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60">
          <h2 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-4 flex items-center gap-2"><span className="text-lg">📋</span> Diligenciar los Datos</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            {/* SEDE DINÁMICA CONECTADA A LA BASE DE DATOS */}
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">Punto de Venta / Sede *</label>
              <select 
                name="pdv" 
                value={datos.pdv} 
                onChange={handleChange} 
                className="w-full bg-white/80 border-white focus:border-amber-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm cursor-pointer"
              >
                <option value="" disabled>-- Selecciona una Sede --</option>
                {listaSedes.map(sede => (
                  <option key={sede.id} value={sede.nombre}>
                    {sede.nombre} {sede.codigo_bodega ? `(Bodega: ${sede.codigo_bodega})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">Fecha *</label>
              <input type="date" name="fecha" value={datos.fecha} onChange={handleChange} className="w-full bg-white/80 border-white focus:border-amber-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">Nombre de quien reporta</label>
              <input type="text" name="nombre_reporta" value={datos.nombre_reporta} readOnly className="w-full bg-slate-100/50 border-white/50 rounded-xl px-3 py-2 text-xs font-bold text-slate-500 outline-none cursor-not-allowed shadow-inner" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">Cargo quien reporta</label>
              <input type="text" name="cargo_reporta" value={datos.cargo_reporta} readOnly className="w-full bg-slate-100/50 border-white/50 rounded-xl px-3 py-2 text-xs font-bold text-slate-500 outline-none cursor-not-allowed shadow-inner" />
            </div>
          </div>
        </div>

        {/* SECCIÓN 2: ¿QUE HA OBSERVADO? */}
        <div className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60">
          <h2 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-4 flex items-center gap-2"><span className="text-lg">👁️</span> ¿Qué ha observado?</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            {['INCIDENTE', 'ACTO INSEGURO', 'CONDICION INSEGURA'].map((tipo) => (
              <label key={tipo} className={`flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${datos.tipo_evento === tipo ? 'bg-amber-100 border-amber-400 text-amber-800 shadow-sm' : 'bg-white/60 border-white/60 text-slate-500 hover:bg-white'}`}>
                <input type="radio" name="tipo_evento" value={tipo} checked={datos.tipo_evento === tipo} onChange={handleChange} className="hidden" />
                <span className="text-[11px] font-black">{tipo}</span>
              </label>
            ))}
          </div>

          <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">Descripción del incidente, acto o condición insegura *</label>
          <textarea name="descripcion" value={datos.descripcion} onChange={handleChange} rows="3" className="w-full bg-white/80 border-white focus:border-amber-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm resize-none"></textarea>
        </div>

        {/* SECCIÓN 3: PELIGROS Y RIESGOS */}
        <div className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60">
          <h2 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-3 flex items-center gap-2">⚠️ Peligros Y Riesgos</h2>
          <p className="text-[10px] font-bold text-slate-500 mb-4">(Marque todo lo que corresponda)</p>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            {LISTA_PELIGROS.map(p => (
              <label key={p} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={datos.peligros.includes(p)} onChange={() => handleCheckbox('peligros', p)} className="accent-amber-500 w-3 h-3" />
                <span className="text-[10px] font-bold text-slate-700">{p}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] font-bold text-slate-700">Otro cual:</span>
            <input type="text" name="peligro_otro" value={datos.peligro_otro} onChange={handleChange} className="flex-1 bg-white/50 border-b border-slate-300 focus:border-amber-500 outline-none text-xs px-1" />
          </div>
        </div>

        {/* SECCIÓN 4: ACTOS INSEGUROS */}
        <div className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60">
          <h2 className="text-sm font-black uppercase tracking-tight mb-4 text-red-600">🛑 Actos Inseguros</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {LISTA_ACTOS.map(a => (
              <label key={a} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={datos.actos.includes(a)} onChange={() => handleCheckbox('actos', a)} className="accent-red-500 w-3 h-3" />
                <span className="text-[10px] font-bold text-slate-700 leading-tight">{a}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] font-bold text-slate-700">Otro acto inseguro:</span>
            <input type="text" name="acto_otro" value={datos.acto_otro} onChange={handleChange} className="flex-1 bg-white/50 border-b border-slate-300 focus:border-red-500 outline-none text-xs px-1" />
          </div>
        </div>

        {/* SECCIÓN 5: CONDICIONES INSEGURAS */}
        <div className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60">
          <h2 className="text-sm font-black uppercase tracking-tight mb-4 text-orange-500">🚧 Condiciones Inseguras</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {LISTA_CONDICIONES.map(c => (
              <label key={c} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={datos.condiciones.includes(c)} onChange={() => handleCheckbox('condiciones', c)} className="accent-orange-500 w-3 h-3" />
                <span className="text-[10px] font-bold text-slate-700 leading-tight">{c}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] font-bold text-slate-700">Otra condición:</span>
            <input type="text" name="condicion_otra" value={datos.condicion_otra} onChange={handleChange} className="flex-1 bg-white/50 border-b border-slate-300 focus:border-orange-500 outline-none text-xs px-1" />
          </div>
        </div>

        {/* SECCIÓN 6: ESTADO Y PLAN DE ACCIÓN */}
        <div className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60">
          <h2 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-4 flex items-center gap-2"><span className="text-lg">🛠️</span> Estado de la Observación y Acción</h2>
          
          <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">¿Que hizo usted para controlar el riesgo?</label>
          <textarea name="accion_control" value={datos.accion_control} onChange={handleChange} rows="2" className="w-full bg-white/80 border-white focus:border-amber-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm mb-4"></textarea>

          <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-200">
            <h3 className="text-xs font-black text-amber-800 uppercase mb-1">PLAN DE ACCIÓN</h3>
            <p className="text-[9px] text-amber-700 mb-3 italic">Las acciones propuestas deben desarrollarse con el apoyo del área SST y/o COPPAST.</p>
            
            <textarea name="plan_accion" value={datos.plan_accion} onChange={handleChange} rows="2" placeholder="Describa el plan de acción..." className="w-full bg-white border-white focus:border-amber-400 rounded-lg px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm mb-3"></textarea>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[9px] font-black text-slate-600 mb-1 uppercase">Responsable de Ejecución</label>
                <input type="text" name="responsable_ejecucion" value={datos.responsable_ejecucion} onChange={handleChange} className="w-full bg-white rounded-lg px-2 py-1.5 text-xs outline-none shadow-sm border border-slate-200 focus:border-amber-400" />
              </div>
              <div>
                <label className="block text-[9px] font-black text-slate-600 mb-1 uppercase">Fecha de Ejecución</label>
                <input type="date" name="fecha_ejecucion" value={datos.fecha_ejecucion} onChange={handleChange} className="w-full bg-white rounded-lg px-2 py-1.5 text-xs outline-none shadow-sm border border-slate-200 focus:border-amber-400" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
              <div>
                <label className="block text-[9px] font-black text-slate-600 mb-1 uppercase">Seguimiento</label>
                <input type="text" name="seguimiento" value={datos.seguimiento} onChange={handleChange} className="w-full bg-white rounded-lg px-2 py-1.5 text-xs outline-none shadow-sm border border-slate-200 focus:border-amber-400" />
              </div>
              <div className="flex gap-4 p-2 bg-white rounded-lg shadow-sm border border-slate-200">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="estado_seguimiento" value="Abierta" checked={datos.estado_seguimiento === "Abierta"} onChange={handleChange} className="accent-amber-500" />
                  <span className="text-[10px] font-bold text-slate-700">Abierta</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="estado_seguimiento" value="Cerrada" checked={datos.estado_seguimiento === "Cerrada"} onChange={handleChange} className="accent-emerald-500" />
                  <span className="text-[10px] font-bold text-slate-700">Cerrada</span>
                </label>
              </div>
            </div>

          </div>
        </div>

        {/* FIRMA Y BOTÓN DE GUARDAR */}
        <div className="border-t border-white/20 pt-6 pb-2 flex flex-col items-center">
          <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-1">Cierre del Reporte</h3>
          <p className="text-[10px] text-slate-600 font-bold mb-4">Nombre y Firma de quien Diligenció</p>
          
          <FirmaPad onSave={setFirmaBase64} onClear={() => setFirmaBase64(null)} />

          <button 
            onClick={handleSubmit}
            className="w-full max-w-sm mt-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-black py-4 rounded-xl shadow-[0_8px_30px_rgba(245,158,11,0.3)] active:scale-95 transition-all text-sm tracking-widest uppercase cursor-pointer border border-amber-300"
          >
            Enviar Reporte
          </button>
        </div>
      </div>

      {/* ALERTAS */}
      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : 'Atención'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className={`w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer ${customAlert.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-500 hover:bg-amber-400'}`}>
                Entendido
              </button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}